import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, Trash2, Mail, Phone, Calendar, Inbox, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authConfig() {
  const token = localStorage.getItem("admin_token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const adminEmail = localStorage.getItem("admin_email") || "";

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin/login", { replace: true });
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, authConfig());
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        return;
      }
      setError("Impossibile caricare le richieste.");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa richiesta?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, authConfig());
      setItems((arr) => arr.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main data-testid="admin-dashboard-page" className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-slate-900 text-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-amber-400">Area riservata</div>
            <div className="font-serif text-2xl">Richieste di contatto</div>
            {adminEmail && (
              <div data-testid="admin-current-user" className="text-xs text-slate-400 mt-1">
                Accesso come {adminEmail}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-back-to-site"
              className="text-sm text-slate-300 hover:text-white"
            >
              Torna al sito →
            </Link>
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-4 py-2 rounded-full text-sm hover:bg-white/10"
            >
              <RefreshCw size={14} /> Aggiorna
            </button>
            <Link
              to="/admin/cambia-password"
              data-testid="admin-change-password-link"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-4 py-2 rounded-full text-sm hover:bg-white/10"
            >
              <KeyRound size={14} /> Password
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-amber-300 text-slate-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-amber-200"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {error && (
          <div data-testid="admin-error" className="p-4 mb-6 bg-red-50 border border-red-200 text-red-800 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div data-testid="admin-loading" className="text-slate-500">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-white border border-slate-200">
            <Inbox size={36} className="mx-auto text-slate-400" />
            <div className="mt-4 font-serif text-2xl text-slate-700">Nessuna richiesta</div>
            <div className="mt-2 text-sm text-slate-500">Le richieste inviate dal modulo di contatto appariranno qui.</div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* List */}
            <div className="lg:col-span-5 xl:col-span-4">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">
                {items.length} {items.length === 1 ? "richiesta" : "richieste"}
              </div>
              <div className="space-y-2">
                {items.map((it) => (
                  <button
                    key={it.id}
                    onClick={() => setSelected(it)}
                    data-testid={`admin-contact-row-${it.id}`}
                    className={`w-full text-left bg-white border p-4 transition-all ${
                      selected?.id === it.id
                        ? "border-slate-900 shadow"
                        : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-medium text-slate-900">{it.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0">
                        {fmtDate(it.timestamp)}
                      </div>
                    </div>
                    <div className="text-sm text-slate-600 mt-1">{it.subject || "(senza oggetto)"}</div>
                    <div className="text-xs text-slate-400 mt-1">{it.email}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Detail */}
            <div className="lg:col-span-7 xl:col-span-8">
              {selected ? (
                <div
                  data-testid="admin-contact-detail"
                  className="bg-white border border-slate-200 p-8 lg:p-10"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="overline">Richiesta</div>
                      <h2 className="font-serif text-3xl mt-2 text-slate-900">{selected.name}</h2>
                      <div className="text-sm text-slate-500 mt-1">{selected.subject || "(senza oggetto)"}</div>
                    </div>
                    <button
                      onClick={() => remove(selected.id)}
                      data-testid="admin-delete-btn"
                      className="inline-flex items-center gap-2 border border-red-300 text-red-700 hover:bg-red-50 px-4 py-2 text-sm transition-colors"
                    >
                      <Trash2 size={14} /> Elimina
                    </button>
                  </div>

                  <div className="mt-8 grid sm:grid-cols-3 gap-4">
                    <div className="border-t-2 border-slate-900 pt-4">
                      <div className="text-[10px] uppercase tracking-wider text-amber-800">Email</div>
                      <a href={`mailto:${selected.email}`} className="text-sm text-slate-900 mt-1 inline-flex items-center gap-1.5 hover:underline">
                        <Mail size={12} /> {selected.email}
                      </a>
                    </div>
                    <div className="border-t-2 border-slate-900 pt-4">
                      <div className="text-[10px] uppercase tracking-wider text-amber-800">Telefono</div>
                      {selected.phone ? (
                        <a href={`tel:${selected.phone}`} className="text-sm text-slate-900 mt-1 inline-flex items-center gap-1.5 hover:underline">
                          <Phone size={12} /> {selected.phone}
                        </a>
                      ) : (
                        <div className="text-sm text-slate-400 mt-1">—</div>
                      )}
                    </div>
                    <div className="border-t-2 border-slate-900 pt-4">
                      <div className="text-[10px] uppercase tracking-wider text-amber-800">Inviata</div>
                      <div className="text-sm text-slate-900 mt-1 inline-flex items-center gap-1.5">
                        <Calendar size={12} /> {fmtDate(selected.timestamp)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-10">
                    <div className="overline mb-3">Messaggio</div>
                    <div className="bg-slate-50 border-l-4 border-amber-700 p-6 text-slate-800 whitespace-pre-wrap text-[15px] leading-relaxed">
                      {selected.message}
                    </div>
                  </div>

                  <div className="mt-8">
                    <a
                      href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Richiesta dal sito")}`}
                      data-testid="admin-reply-btn"
                      className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-amber-800 transition-colors"
                    >
                      <Mail size={14} /> Rispondi al cliente
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500">
                  Seleziona una richiesta dalla lista per vederne i dettagli.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
